import { getRelationshipLabel } from "src/components/explore/filter-popover/utils";
import { findDuplicates } from "src/utils/find-duplicates";

const THROUGH_PATH_SEPARATOR = " → ";

type ThroughPathSegment = {
  from_model: { id: unknown };
  to_model: { id: unknown };
};

type LabeledRelationship = {
  name: string | null;
  from_model: { id: unknown };
  to_model: { name: string };
  through_relationships: ReadonlyArray<{
    relationship: ThroughPathSegment & {
      name: string | null;
      to_model: { name: string };
    };
  }>;
};

/**
 * Joins through-relationship path segment names, e.g. "Mobile Lines → Product Activity".
 */
export function formatThroughPath(segmentNames: readonly string[]): string;
export function formatThroughPath(
  segmentNames: readonly string[] | null | undefined,
): string | undefined;
export function formatThroughPath(
  segmentNames: readonly string[] | null | undefined,
): string | undefined {
  return segmentNames?.join(THROUGH_PATH_SEPARATOR);
}

/**
 * A path segment is named after its relationship, or else the model it leads to.
 */
export function getThroughPathSegmentName(segment: {
  name: string | null;
  to_model: { name: string };
}): string {
  return segment.name ?? segment.to_model.name;
}

/**
 * A relationship is named after its target model until someone renames it, and
 * every relationship to that model shares that default.
 */
export function isDefaultRelationshipName(
  name: string | null | undefined,
  toModelName: string | null | undefined,
): boolean {
  return !name || name === toModelName;
}

/**
 * Orders a through relationship's path segments by following them from
 * `startModelId`. The rows that store them have no order column.
 */
export function orderThroughPathSegments<T extends ThroughPathSegment>(
  startModelId: string,
  segments: readonly T[],
): T[] {
  const segmentsByFromModelId = new Map(
    segments.map((segment) => [String(segment.from_model.id), segment]),
  );
  const visitedModelIds = new Set<string>();
  const path: T[] = [];
  let modelId = startModelId;
  let segment = segmentsByFromModelId.get(modelId);
  while (segment && !visitedModelIds.has(modelId)) {
    visitedModelIds.add(modelId);
    path.push(segment);
    modelId = String(segment.to_model.id);
    segment = segmentsByFromModelId.get(modelId);
  }
  return path;
}

/**
 * Returns a function that labels a relationship with its target model's name, plus "(via <name>)" when that model name repeats in `relationships`.
 * The name is the relationship's custom name, or else its through path. A direct relationship without a custom name gets no suffix.
 */
export function createRelationshipOptionLabeler<R extends LabeledRelationship>(
  relationships: R[],
): (relationship: R) => string {
  const duplicateModelNames = findDuplicates(
    relationships,
    (relationship) => relationship.to_model.name,
  );
  return (relationship) => {
    const modelName = relationship.to_model.name;
    if (!duplicateModelNames.has(modelName)) {
      return modelName;
    }
    return getRelationshipLabel(getDistinguishingName(relationship), modelName);
  };
}

function getDistinguishingName(
  relationship: LabeledRelationship,
): string | null {
  const name = relationship.name?.trim() ?? null;
  if (!isDefaultRelationshipName(name, relationship.to_model.name)) {
    return name;
  }
  if (relationship.through_relationships.length === 0) {
    return null;
  }
  return formatThroughPath(
    orderThroughPathSegments(
      String(relationship.from_model.id),
      relationship.through_relationships.map(
        ({ relationship: segment }) => segment,
      ),
    ).map(getThroughPathSegmentName),
  );
}
